import {
  IconDefinition,
  faCircleExclamation,
  faMicrophoneSlash,
  faPlugCircleXmark,
  faSignal,
  faVideoSlash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { useTranslation } from 'next-i18next'
import COLORS from '../../../styles/colors'
import { MonitoredParticipant } from '../hooks/useLiveCallMonitorData'
import {
  DeviceHealth,
  DeviceIssueCode,
  HealthLevel,
  healthRank,
} from '../util/deviceStatus'

const WARNING_COLOR = '#E8A100'

const LEVEL_COLORS: Record<HealthLevel, string> = {
  unknown: COLORS.grey,
  ok: COLORS.green,
  warning: WARNING_COLOR,
  critical: COLORS.red,
}

const ISSUE_ICONS: Record<DeviceIssueCode, IconDefinition> = {
  micUnavailable: faMicrophoneSlash,
  micNoInput: faMicrophoneSlash,
  cameraUnavailable: faVideoSlash,
  notConnected: faPlugCircleXmark,
  notReporting: faPlugCircleXmark,
  networkUplinkPoor: faSignal,
  networkUplinkDown: faSignal,
  networkDownlinkPoor: faSignal,
  networkDownlinkDown: faSignal,
}

const sortedIssues = (health: DeviceHealth) =>
  [...health.issues].sort((a, b) => healthRank(b.level) - healthRank(a.level))

export type ParticipantHealthIconProps = { health: DeviceHealth }

export const ParticipantHealthIcon = ({
  health,
}: ParticipantHealthIconProps) => {
  const { t } = useTranslation('LiveCallMonitor')

  const topIssue = sortedIssues(health)[0]
  const status = health.status
  const showDetails =
    status &&
    (status.sendBitrateKbps != null ||
      status.recvBitrateKbps != null ||
      status.rttMs != null)

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        minHeight: '20px',
      }}
    >
      {health.level !== 'unknown' && (
        <Tooltip
          arrow
          placement="top"
          title={
            <Stack spacing="2px">
              {health.issues.length === 0 && (
                <Typography fontSize="12px">{t('health.ok')}</Typography>
              )}
              {sortedIssues(health).map((issue) => (
                <Typography key={issue.code} fontSize="12px">
                  {t(`health.${issue.code}`)}
                </Typography>
              ))}
              {showDetails && (
                <Typography fontSize="12px" sx={{ opacity: 0.8 }}>
                  {t('health.networkDetails', {
                    send: status?.sendBitrateKbps ?? '-',
                    recv: status?.recvBitrateKbps ?? '-',
                    rtt: status?.rttMs ?? '-',
                  })}
                </Typography>
              )}
            </Stack>
          }
        >
          <span>
            <FontAwesomeIcon
              aria-label={t(`health.level.${health.level}`) as string}
              color={LEVEL_COLORS[health.level]}
              icon={topIssue ? ISSUE_ICONS[topIssue.code] : faSignal}
              style={{ height: '14px' }}
            />
          </span>
        </Tooltip>
      )}
    </Box>
  )
}

export type ReadingHealthBadgeProps = { participants: MonitoredParticipant[] }

const MAX_LISTED = 5

// A Book is the presenter, so any problem on their side counts as critical for the Reading.
export const getReadingHealthLevel = (
  participants: MonitoredParticipant[],
): HealthLevel => {
  const withIssues = participants.filter(
    ({ health }) => health.level === 'warning' || health.level === 'critical',
  )
  if (withIssues.length === 0) return 'ok'

  return withIssues.some(
    ({ health, role }) => health.level === 'critical' || role === 'book',
  )
    ? 'critical'
    : 'warning'
}

export const ReadingHealthBadge = ({
  participants,
}: ReadingHealthBadgeProps) => {
  const { t } = useTranslation('LiveCallMonitor')

  const withIssues = participants.filter(
    ({ health }) => health.level === 'warning' || health.level === 'critical',
  )
  if (withIssues.length === 0) return null

  const level = getReadingHealthLevel(participants)

  return (
    <Tooltip
      arrow
      title={
        <Stack spacing="2px">
          <Typography fontSize="12px" fontWeight={600}>
            {t('health.readingSummary', { count: withIssues.length })}
          </Typography>
          {withIssues.slice(0, MAX_LISTED).map((participant) => (
            <Typography key={participant.user_id} fontSize="12px">
              {`${participant.personName || ''}: ${t(
                `health.${sortedIssues(participant.health)[0].code}`,
              )}`}
            </Typography>
          ))}
          {withIssues.length > MAX_LISTED && (
            <Typography fontSize="12px">
              {t('health.andMore', { count: withIssues.length - MAX_LISTED })}
            </Typography>
          )}
        </Stack>
      }
    >
      <span>
        <FontAwesomeIcon
          aria-label={
            t('health.readingSummary', {
              count: withIssues.length,
            }) as string
          }
          color={LEVEL_COLORS[level]}
          icon={faCircleExclamation}
        />
      </span>
    </Tooltip>
  )
}
